import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { RootState } from "../../store";
import {
  fetchCities,
  createCity,
  editCity,
  selectCities,
  selectCityError,
  selectCityLoading,
  resetForm,
  selectCityPagination,
  toggleCityStatus
} from "./citySlice";
import { fetchStates, selectStates } from "../state/stateSlice";
import { useAppDispatch } from "../../store";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { usePermission } from "../../components/common/PermissionGuard";
import PageWrapper from "../../components/common/PageWrapper";
import { City } from "./city";
import dayjs from "dayjs";
import SearchBar from "../../components/searchbar";
import Pagination from '../../components/common/Pagination';
import DropdownField from "../../form/DropdownField";
import ChangeStatusConfirmation from "../../components/common/__tests__/ChangeStatusConfirmation";


export const Cities: React.FC = () => {
  const dispatch = useAppDispatch();
  const cities = useSelector(selectCities);
  const states = useSelector(selectStates);
  const loading = useSelector(selectCityLoading);
  const error = useSelector(selectCityError);
  const pagination = useSelector(selectCityPagination)

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [cityName, setCityName] = useState("");
  const [cityStatus, setCityStatus] = useState(1);
  const [selectedStateId, setSelectedStateId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedCityForStatus, setSelectedCityForStatus] = useState<City | null>(null);

  // Permissions
  const canCreate = usePermission("cities", "create");
  const canUpdate = usePermission("cities", "update");
  const canRead = usePermission("cities", "read");

  // useEffect(() => {
  //   dispatch(fetchCities({}));
  //   dispatch(fetchStates({ limit: 10000 })); // Fetch all states for dropdown
  // }, [dispatch]);

    useEffect(() => {
      dispatch(fetchCities({ page: currentPage, limit: itemsPerPage }));
      dispatch(fetchStates({ limit: 10000 })); // Fetch all states for dropdown
    }, [dispatch, currentPage, itemsPerPage]);
  

  const handleCreateCity = () => {
    dispatch(resetForm());
    setSelectedCity(null);
    setFormMode("create");
    setCityName('');
    setCityStatus(1);
    setSelectedStateId("");
    setIsFormOpen(true);
  };

  const handleEditCity = (city: City) => {
    setCityName(city.city_name);
    setCityStatus(city.status);
    setSelectedStateId(city.state_id?.toString() || "");
    setSelectedCity(city);
    setFormMode("edit");
    setIsFormOpen(true);
  };

  const handleFormSubmit = () => {
    if (!selectedStateId || !cityName.trim()) {
      alert("Please select a state and enter city name");
      return;
    }

    const formData = {
      state_id: Number(selectedStateId),
      city_name: cityName,
      status: cityStatus,
    };

    if (formMode === "create") {
      dispatch(createCity(formData));
    } else if (selectedCity) {
      dispatch(editCity({ id: selectedCity.id!, data: formData }));
    }

    setIsFormOpen(false);
  };

  const toggleStatus = async (city: City) => {
    const newStatus = city.status === 1 ? 0 : 1;
    try {
      await dispatch(toggleCityStatus({
        id: city.cityId,
        status: newStatus
      })).unwrap();
     
      dispatch(fetchCities({
        page: currentPage,
        limit: itemsPerPage
      }));
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  return (
    <PageWrapper>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              City Management
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage cities for your application
            </p>
          </div>
          {canCreate && (
            <button
              onClick={handleCreateCity}
              className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create City
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="p-6">
            <SearchBar
              placeholder="Search city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      City Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      State
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Last Updated On
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Last Updated By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {cities.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
                      >
                        No cities found.
                      </td>
                    </tr>
                  ) : (
                    cities
                      .filter((c) =>
                        c.city_name
                          ?.toLowerCase()
                          .includes(searchTerm.toLowerCase())
                      )
                      .map((city) => (
                        <tr key={city.id}>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {city.city_name}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {city?.state_name || "N/A"}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {(() => {
                              // Show createdOn if record was never updated (bulk uploaded)
                              // Otherwise show updatedOn
                              const displayDate = city?.updatedOn && 
                                city?.createdOn && 
                                dayjs(city.updatedOn).isAfter(dayjs(city.createdOn))
                                ? city.updatedOn 
                                : city?.createdOn || city?.updatedOn;
                              return displayDate ? dayjs(displayDate).format("DD/MM/YYYY HH:mm") : 'N/A';
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {city?.user_name || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm cursor-pointer" onClick={() => {
                            setSelectedCityForStatus(city);
                            setIsStatusModalOpen(true);
                          }}>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                city.status === 1
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {city.status === 1 ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            {canUpdate && (
                              <button
                                onClick={() => handleEditCity(city)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {formMode === "create" ? "Create City" : "Edit City"}
                </h3>
                <div className="space-y-4">
                  {/* State dropdown */}
                  <DropdownField
                    label="Select State"
                    name="selectedStateId"
                    value={selectedStateId}
                    required
                    options={states.map((state) => ({
                      label: state.state_name,
                      value: state.id.toString(),
                    }))}
                    placeholder="Select a state"
                    onChange={(e) => setSelectedStateId(e.target.value)}
                  />

                  {/* City Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City Name
                    </label>
                    <input
                      type="text"
                      value={cityName}
                      onChange={(e) => setCityName(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="Enter city name"
                    />
                  </div>

                  {/* Status */}
                  {/* <DropdownField
                    label="Status"
                    name="cityStatus"
                    value={cityStatus.toString()}
                    required
                    options={[
                      { label: "Active", value: "1" },
                      { label: "Inactive", value: "0" },
                    ]}
                    placeholder="Select status"
                    onChange={(e) => setCityStatus(Number(e.target.value))}
                  /> */}
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setIsFormOpen(false)}
                    className="px-4 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleFormSubmit}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {formMode === "create" ? "Create" : "Update"}
                  </button>
                </div>
              </div>
              <button
                onClick={() => setIsFormOpen(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
        )}
      </div>

      {isStatusModalOpen && selectedCityForStatus && (
        <ChangeStatusConfirmation
          open={isStatusModalOpen}
          onClose={() => {
            setIsStatusModalOpen(false);
            setSelectedCityForStatus(null);
          }}
          onConfirm={() => {
            toggleStatus(selectedCityForStatus);
            setIsStatusModalOpen(false);
            setSelectedCityForStatus(null);
          }}
          title="Change Status"
          message={`Are you sure you want to change status to ${selectedCityForStatus.status === 1 ? 'Inactive' : 'Active'}?`}
        />
      )}

      {pagination && pagination.total > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => {
            setCurrentPage(page);
            dispatch(fetchCities({ page, limit: itemsPerPage }));
          }}
          onItemsPerPageChange={(limit) => {
            setItemsPerPage(limit);
            setCurrentPage(1);
            dispatch(fetchCities({ page: 1, limit }));
          }}
          showItemsPerPage={true}
        />
      )}

    </PageWrapper>
  );
};
